import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Paper,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  ExpandMore as ExpandMoreIcon,
  PictureAsPdf as PdfIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';

import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';

const AdminDocumentsTab = ({ user }) => {
  const { tokenManager } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documentOpen, setDocumentOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);

  // Document management states from AdminEmployeeManagement
  const [selectedCountry, setSelectedCountry] = useState('');
  const [documentRequirements, setDocumentRequirements] = useState([]);
  const [processDocuments, setProcessDocuments] = useState([]);
  const [loadingProcessDocuments, setLoadingProcessDocuments] = useState(false);
  const [loadingDocumentRequirements, setLoadingDocumentRequirements] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // HR Documents states
  const [hrDocuments, setHrDocuments] = useState([]);
  const [loadingHrDocuments, setLoadingHrDocuments] = useState(false);
  const [hrUploadDialogOpen, setHrUploadDialogOpen] = useState(false);
  const [selectedHrDocumentType, setSelectedHrDocumentType] = useState('');
  const [hrFile, setHrFile] = useState(null);
  const [uploadingHr, setUploadingHr] = useState(false);

  // Onboarding Form states
  const [onboardingFormData, setOnboardingFormData] = useState(null);
  const [loadingOnboardingForm, setLoadingOnboardingForm] = useState(false);
  const [onboardingFormError, setOnboardingFormError] = useState(null);

  // Available countries for selection
  const availableCountries = [
    { value: 'India', label: 'India' },
    { value: 'Vietnam', label: 'Vietnam' },
    { value: 'Indonesia', label: 'Indonesia' },
  ];

  // HR Document types
  const HR_DOCUMENT_TYPES = {
    NDA: {
      name: 'NDA',
      module: 'Process Document',
      subModule: 'Hr Document',
      description: 'Non-Disclosure Agreement'
    },
    APPOINTMENT_LETTER: {
      name: 'Appointment Letter',
      module: 'Process Document',
      subModule: 'Hr Document',
      description: 'Official appointment letter'
    },
    KRI_KPA: {
      name: 'KRI/KPA',
      module: 'Process Document',
      subModule: 'Hr Document',
      description: 'Key Result Indicators/Key Performance Areas'
    }
  };

  // Fetch onboarding form when user changes
  useEffect(() => {
    if (user?.id || user?._id) {
      fetchOnboardingForm();
    } else {
      setOnboardingFormData(null);
    }
  }, [user]);

  // Fetch documents for the selected user
  useEffect(() => {
    if (user?.id || user?._id) {
      fetchDocuments();
    }
  }, [user]);

  // Fetch document requirements when country changes
  useEffect(() => {
    if (selectedCountry) {
      fetchDocumentRequirements(selectedCountry);
    }
  }, [selectedCountry]);

  // Fetch process documents when user and country are selected
  useEffect(() => {
    if (user && selectedCountry) {
      fetchProcessDocuments();
    }
  }, [user, selectedCountry]);

  // Fetch HR documents when user is selected
  useEffect(() => {
    if (user) {
      fetchHrDocuments();
    }
  }, [user]);

  const fetchOnboardingForm = async () => {
    if (!user) return;
    const userId = user._id || user.id;
    setLoadingOnboardingForm(true);
    setOnboardingFormError(null);
    try {
      const response = await fetch(`${API_URL}/onboarding/onboarding-form/${userId}`, {
        headers: { 'Authorization': `Bearer ${tokenManager.getToken()}` },
      });
      if (response.ok) {
        const result = await response.json();
        setOnboardingFormData(result.data || null);
      } else {
        setOnboardingFormError('Failed to load onboarding form data.');
      }
    } catch (err) {
      setOnboardingFormError('Network error while loading onboarding form.');
    } finally {
      setLoadingOnboardingForm(false);
    }
  };

  const handleDownloadOnboardingFormPDF = () => {
    if (!onboardingFormData) return;
    const fd = onboardingFormData;
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : 'Not provided';
    const val = (v) => v || 'Not provided';

    const row = (label, value) =>
      `<tr><td style="padding:6px 10px;font-weight:600;color:#555;width:200px;border:1px solid #ddd;background:#f9f9f9">${label}</td><td style="padding:6px 10px;border:1px solid #ddd">${val(value)}</td></tr>`;

    const sectionTitle = (title) =>
      `<tr><td colspan="2" style="background:#1976d2;color:#fff;padding:8px 10px;font-size:14px;font-weight:700;letter-spacing:0.5px">${title}</td></tr>`;

    // Work Experience rows
    const workExpRows = (fd.workExperiences || []).length > 0
      ? (fd.workExperiences || []).map((exp, i) => `
          ${sectionTitle(`Work Experience ${i + 1}`)}
          ${row('Organization', exp.organizationName)}
          ${row('Designation', exp.designation)}
          ${row('From Date', fmtDate(exp.fromDate))}
          ${row('To Date', fmtDate(exp.toDate))}
          ${row('Contact Number', exp.contactNumber)}
          ${row('Job Description', exp.jobDescription)}
        `).join('')
      : `${sectionTitle('Work Experience')}${row('', 'No work experience provided')}`;

    // Family members rows
    const familyRows = (fd.familyMembers || []).length > 0
      ? (fd.familyMembers || []).map((m, i) => `
          ${sectionTitle(`Family Member ${i + 1}`)}
          ${row('Name', m.name)}
          ${row('Date of Birth', fmtDate(m.dateOfBirth))}
          ${row('Relation', m.relation)}
          ${row('Residing with self', m.residingWithSelf ? 'Yes' : 'No')}
        `).join('')
      : `${sectionTitle('Family Members')}${row('', 'No family members provided')}`;

    // Sensitive fields based on country
    let sensitiveRows = '';
    if (fd.country === 'India') {
      sensitiveRows = `
        ${row('Aadhaar Number', fd.aadharNumber)}
        ${row('PAN Card Number', fd.panCardNumber)}
        ${row('UAN Number', fd.pfDetails?.uanNumber)}
      `;
    } else {
      sensitiveRows = `
        ${row('Tax Identification Number', fd.taxIdentificationNumber)}
        ${row('National Identity Number', fd.nationalIdentityNumber)}
        ${row('Social Security Number', fd.pfDetails?.socialSecurityNumber)}
      `;
    }

    const permanentAddressRows = fd.permanentAddress?.sameAsCurrent
      ? row('Permanent Address', 'Same as current address')
      : `
        ${row('Permanent Street', fd.permanentAddress?.street)}
        ${row('Permanent City', fd.permanentAddress?.city)}
        ${row('Permanent State', fd.permanentAddress?.state)}
        ${row('Permanent Pin-code', fd.permanentAddress?.zipCode)}
        ${row('Permanent Country', fd.permanentAddress?.country)}
      `;

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Onboarding Form - ${val(fd.employeeName)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 13px; color: #333; padding: 20px; }
    h1 { text-align: center; font-size: 20px; margin-bottom: 4px; color: #1976d2; }
    .subtitle { text-align: center; font-size: 13px; color: #777; margin-bottom: 20px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 0; }
    @media print {
      body { padding: 10px; }
      button { display: none; }
      @page { margin: 15mm; size: A4; }
    }
  </style>
</head>
<body>
  <h1>Employee Onboarding Form</h1>
  <p class="subtitle">Generated on ${new Date().toLocaleDateString('en-IN')} &nbsp;|&nbsp; ${val(fd.employeeName)} &nbsp;|&nbsp; ${val(fd.employeeId)}</p>
  <table>
    ${sectionTitle('PERSONAL INFORMATION')}
    ${row('Employee ID', fd.employeeId)}
    ${row('Employee Name', fd.employeeName)}
    ${row('Sex', fd.sex)}
    ${row('Date of Joining', fmtDate(fd.dateOfJoining))}
    ${row('Country', fd.country)}
    ${row('Nationality', fd.nationality)}
    ${row('Date of Birth', fmtDate(fd.dateOfBirth))}
    ${row('Marital Status', fd.maritalStatus)}
    ${row('Blood Group', fd.bloodGroup)}
    ${row('Reporting Manager', fd.reportingManager)}
    ${row('Unit Head', fd.unitHead)}

    ${sectionTitle('CURRENT ADDRESS')}
    ${row('Street', fd.currentAddress?.street)}
    ${row('City', fd.currentAddress?.city)}
    ${row('State', fd.currentAddress?.state)}
    ${row('Pin-code', fd.currentAddress?.zipCode)}
    ${row('Country', fd.currentAddress?.country)}

    ${sectionTitle('PERMANENT ADDRESS')}
    ${permanentAddressRows}

    ${sectionTitle('CONTACT INFORMATION')}
    ${row('Email ID', fd.emailId)}
    ${row('Mobile Number', fd.mobileNumber)}
    ${row('Emergency Contact Name', fd.emergencyContact?.name)}
    ${row('Emergency Contact Number', fd.emergencyContact?.contactNo)}
    ${row('Relationship with Employee', fd.emergencyContact?.relationshipWithEmployee)}
    ${row('Passport No.', fd.passportNo)}

    ${sectionTitle('IDENTITY & PF DETAILS')}
    ${sensitiveRows}

    ${workExpRows}
    ${familyRows}

    ${sectionTitle('BANK DETAILS')}
    ${row('Bank Name', fd.bankDetails?.bankName)}
    ${row('Branch Address', fd.bankDetails?.branchAddress)}
    ${row('Account Name', fd.bankDetails?.accountName)}
    ${row('Account No', fd.bankDetails?.accountNo)}
    ${fd.country === 'India' ? row('IFSC Code', fd.bankDetails?.ifscCode) : row('Swift Code / Bank Code', fd.bankDetails?.swiftCode)}

    ${sectionTitle('DECLARATION')}
    ${row('Declaration Accepted', fd.declaration ? 'Yes - Employee has accepted the declaration' : 'No')}
  </table>
</body>
</html>`;

    const win = window.open('', '_blank', 'width=900,height=700');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 500);
  };

  const fetchDocuments = async () => {
    if (!user?.id && !user?._id) return;

    setLoadingDocuments(true);
    try {
      const userId = user._id || user.id;
      
      // Fetch from Employee Document submodule
      const employeeQueryParams = new URLSearchParams();
      employeeQueryParams.append('module', 'Process Document');
      employeeQueryParams.append('subModule', 'Employee Document');
      employeeQueryParams.append('userId', userId);

      const employeeResponse = await fetch(`${API_URL}/documents/user?${employeeQueryParams}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      // Fetch from HR Document submodule
      const hrQueryParams = new URLSearchParams();
      hrQueryParams.append('module', 'Process Document');
      hrQueryParams.append('subModule', 'Hr Document');
      hrQueryParams.append('userId', userId);

      const hrResponse = await fetch(`${API_URL}/documents/user?${hrQueryParams}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      // Fetch from Existing Document submodule
      const existingQueryParams = new URLSearchParams();
      existingQueryParams.append('module', 'Process Document');
      existingQueryParams.append('subModule', 'Existing Document');
      existingQueryParams.append('userId', userId);

      const existingResponse = await fetch(`${API_URL}/documents/user?${existingQueryParams}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      let allDocuments = [];

      if (employeeResponse.ok) {
        const employeeResult = await employeeResponse.json();
        allDocuments = [...(employeeResult.documents || [])];
      }

      if (hrResponse.ok) {
        const hrResult = await hrResponse.json();
        allDocuments = [...allDocuments, ...(hrResult.documents || [])];
      }

      if (existingResponse.ok) {
        const existingResult = await existingResponse.json();
        allDocuments = [...allDocuments, ...(existingResult.documents || [])];
      }

      console.log('Fetched all documents for user:', user.firstName, allDocuments);
      setDocuments(allDocuments);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const fetchDocumentRequirements = async (country) => {
    setLoadingDocumentRequirements(true);
    try {
      const response = await fetch(`${API_URL}/document-requirements/country/${country}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setDocumentRequirements(result.requirements || []);
      } else {
        console.warn('Failed to fetch document requirements from API');
        setDocumentRequirements([]);
      }
    } catch (error) {
      console.error('Error fetching document requirements:', error);
      setDocumentRequirements([]);
    } finally {
      setLoadingDocumentRequirements(false);
    }
  };

  const fetchProcessDocuments = async () => {
    if (!user) return;

    setLoadingProcessDocuments(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('module', 'Process Document');
      queryParams.append('subModule', 'Employee Document');
      queryParams.append('userId', user._id || user.id);

      const response = await fetch(`${API_URL}/documents/user?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setProcessDocuments(result.documents || []);
      }
    } catch (error) {
      console.error('Error fetching process documents:', error);
    } finally {
      setLoadingProcessDocuments(false);
    }
  };

  const fetchHrDocuments = async () => {
    if (!user) return;

    setLoadingHrDocuments(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('module', 'Process Document');
      queryParams.append('subModule', 'Hr Document');
      queryParams.append('userId', user._id || user.id);

      const response = await fetch(`${API_URL}/documents/user?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setHrDocuments(result.documents || []);
      }
    } catch (error) {
      console.error('Error fetching HR documents:', error);
    } finally {
      setLoadingHrDocuments(false);
    }
  };

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file extension - allow PDF and image files
      const fileName = selectedFile.name.toLowerCase();
      const fileExtension = fileName.split('.').pop();
      const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'];
      
      if (!allowedExtensions.includes(fileExtension)) {
        toast.error('Only PDF and image files (JPG, JPEG, PNG, GIF, WEBP) are allowed. Please convert your document to one of these formats and try again.');
        setFile(null);
        // Clear the file input
        e.target.value = '';
        return;
      }
      
      // Check if selected document type requires image only
      if (selectedDocumentType) {
        const documentConfig = documentRequirements.find(doc => doc.id === selectedDocumentType);
        if (documentConfig && documentConfig.imageOnly && fileExtension === 'pdf') {
          toast.error(`${documentConfig.label} must be an image file. PDF is not allowed for this document type.`);
          setFile(null);
          e.target.value = '';
          return;
        }
      }
      
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (selectedFile.size > maxSize) {
        toast.error('File size too large. Maximum file size is 10MB.');
        setFile(null);
        // Clear the file input
        e.target.value = '';
        return;
      }
      
      setFile(selectedFile);
    }
  };

  const handleProcessUpload = async () => {
    if (!file || !selectedDocumentType || !user) {
      toast.error('Please select file, document type, and user');
      return;
    }

    const documentConfig = documentRequirements.find(
      doc => doc.id === selectedDocumentType
    );
    
    if (!documentConfig) {
      toast.error('Invalid document type selected');
      return;
    }

    // Check if document already exists
    const existingDoc = processDocuments.find(doc => doc.documentName === documentConfig.label);
    if (existingDoc) {
      toast.error('This document type has already been uploaded');
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('module', 'Process Document');
      uploadData.append('subModule', 'Employee Document');
      uploadData.append('documentName', documentConfig.label);
      uploadData.append('description', documentConfig.description);
      uploadData.append('uploadedBy', user.email);
      uploadData.append('userId', user._id || user.id);

      const response = await fetch(`${API_URL}/documents/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
          // Don't set Content-Type for FormData - let browser set it with boundary
        },
        body: uploadData,
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Document uploaded successfully');
        setUploadDialogOpen(false);
        setFile(null);
        setSelectedDocumentType('');
        
        // Add the new document to local state immediately
        const newDocument = result.document;
        setProcessDocuments(prevDocs => [...prevDocs, newDocument]);
      } else {
        toast.error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // HR Document upload functions
  const handleHrFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file size (10MB limit)
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        setHrFile(null);
        e.target.value = '';
        return;
      }

      // Check file extension - allow PDF and image files
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'];
      
      if (!allowedExtensions.includes(fileExtension)) {
        toast.error('Only PDF and image files are allowed');
        setHrFile(null);
        e.target.value = '';
        return;
      }

      setHrFile(selectedFile);
    }
  };

  const handleHrUpload = async () => {
    if (!hrFile || !selectedHrDocumentType || !user) {
      toast.error('Please select file, document type, and user');
      return;
    }

    if (!user || !user.email) {
      toast.error('User data is missing. Please select a user again.');
      return;
    }

    const documentConfig = HR_DOCUMENT_TYPES[selectedHrDocumentType];
    
    if (!documentConfig) {
      toast.error('Invalid document type selected');
      return;
    }

    // Check if document already exists
    const existingDoc = hrDocuments.find(doc => doc.documentName === documentConfig.name);
    if (existingDoc) {
      toast.error('This document type has already been uploaded');
      return;
    }

    setUploadingHr(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', hrFile);
      uploadData.append('module', documentConfig.module);
      uploadData.append('subModule', documentConfig.subModule);
      uploadData.append('documentName', documentConfig.name);
      uploadData.append('description', documentConfig.description || '');
      uploadData.append('uploadedBy', user.email);
      uploadData.append('userId', user._id || user.id);

      const response = await fetch(`${API_URL}/documents/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
          // Don't set Content-Type for FormData - let browser set it with boundary
        },
        body: uploadData,
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('HR Document uploaded successfully');
        setHrUploadDialogOpen(false);
        setHrFile(null);
        setSelectedHrDocumentType('');
        
        // Add the new document to local state immediately
        const newDocument = result.document;
        setHrDocuments(prevDocs => [...prevDocs, newDocument]);
      } else {
        console.error('HR Upload failed:', result);
        toast.error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
    } finally {
      setUploadingHr(false);
    }
  };

  const handleViewDocument = (doc) => {
    setCurrentDocument(doc);
    setDocumentOpen(true);
  };

  const handleCloseDocument = () => {
    setDocumentOpen(false);
    setCurrentDocument(null);
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const fileName = doc.filePath || doc.fileName;
      
      if (!fileName) {
        toast.error('File not found');
        return;
      }

      // Show downloading toast
      toast.info(`Downloading ${doc.documentName || fileName}...`);

      // Fetch the file with authorization header first
      const response = await fetch(`${API_URL}/documents/download/${fileName}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Authentication failed. Please login again.');
        } else {
          toast.error('Download failed');
        }
        return;
      }

      // Get the blob from the response
      const blob = await response.blob();
      
      // Determine file extension from original filename or content type
      let fileExtension = '';
      if (fileName.includes('.')) {
        fileExtension = fileName.split('.').pop().toLowerCase();
      } else {
        // Fallback based on content type
        const contentType = response.headers.get('content-type');
        if (contentType) {
          if (contentType.includes('image/jpeg') || contentType.includes('image/jpg')) {
            fileExtension = 'jpg';
          } else if (contentType.includes('image/png')) {
            fileExtension = 'png';
          } else if (contentType.includes('image/gif')) {
            fileExtension = 'gif';
          } else if (contentType.includes('image/webp')) {
            fileExtension = 'webp';
          } else if (contentType.includes('application/pdf')) {
            fileExtension = 'pdf';
          }
        }
      }
      
      // Get the filename from Content-Disposition header (backend handles proper formatting)
      let downloadName = doc.documentName || fileName;
      
      // Try to get the formatted filename from the response header
      const contentDisposition = response.headers.get('content-disposition');
      
      if (contentDisposition) {
        // Extract filename from Content-Disposition header
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch[1]) {
          downloadName = filenameMatch[1];
        }
      }
      
      // If no proper filename from header, fallback to document name with extension
      if (!contentDisposition || !contentDisposition.includes('filename=')) {
        if (fileExtension && !downloadName.toLowerCase().endsWith(`.${fileExtension}`)) {
          downloadName += `.${fileExtension}`;
        }
      }
      
      // Create a download link with unique identifier
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      link.style.display = 'none';
      link.id = `download-link-${Date.now()}`; // Unique ID
      
      // Add to DOM and trigger download
      document.body.appendChild(link);
      
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        link.click();
        
        // Clean up after download starts
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          window.URL.revokeObjectURL(url);
        }, 500); // Increased delay for Chrome
      });

    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    }
  };

  const handleDelete = async (documentId) => {
    try {
      const response = await fetch(`${API_URL}/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        // Remove from local state
        setDocuments((prev) => prev.filter((doc) => (doc.documentId || doc.id) !== documentId));
        toast.success('Document deleted successfully!');
      } else {
        const result = await response.json();
        toast.error(result.error || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete document');
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'verified':
        return (
          <Chip
            icon={<CheckCircleIcon />}
            label="Verified"
            color="success"
            size="small"
          />
        );
      case 'pending':
        return (
          <Chip
            icon={<WarningIcon />}
            label="Pending"
            color="warning"
            size="small"
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Employee Document Management - {user?.firstName} {user?.lastName}
      </Typography>

      {/* Onboarding Form Section */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontSize: '20px', fontWeight: 600 }}>
              Onboarding Form
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PdfIcon />}
            onClick={handleDownloadOnboardingFormPDF}
            disabled={!onboardingFormData || loadingOnboardingForm}
            sx={{ fontSize: '14px', fontWeight: 600 }}
          >
            Download as PDF
          </Button>
        </Box>

        {loadingOnboardingForm ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : onboardingFormError ? (
          <Alert severity="error">{onboardingFormError}</Alert>
        ) : !onboardingFormData ? (
          <Alert severity="info">
            No onboarding form submitted yet for this employee.
          </Alert>
        ) : (
          <Box>
            {/* Personal Information */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>Personal Information</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {[
                    ['Employee ID', onboardingFormData.employeeId],
                    ['Employee Name', onboardingFormData.employeeName],
                    ['Sex', onboardingFormData.sex],
                    ['Date of Joining', onboardingFormData.dateOfJoining ? new Date(onboardingFormData.dateOfJoining).toLocaleDateString() : null],
                    ['Country', onboardingFormData.country],
                    ['Nationality', onboardingFormData.nationality],
                    ['Date of Birth', onboardingFormData.dateOfBirth ? new Date(onboardingFormData.dateOfBirth).toLocaleDateString() : null],
                    ['Marital Status', onboardingFormData.maritalStatus],
                    ['Blood Group', onboardingFormData.bloodGroup],
                    ['Reporting Manager', onboardingFormData.reportingManager],
                    ['Unit Head', onboardingFormData.unitHead],
                  ].map(([label, value]) => (
                    <Grid item xs={12} sm={6} key={label}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{value || 'Not provided'}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Address */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>Address</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Current Address</Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {[
                    ['Street', onboardingFormData.currentAddress?.street],
                    ['City', onboardingFormData.currentAddress?.city],
                    ['State', onboardingFormData.currentAddress?.state],
                    ['Pin-code', onboardingFormData.currentAddress?.zipCode],
                    ['Country', onboardingFormData.currentAddress?.country],
                  ].map(([label, value]) => (
                    <Grid item xs={12} sm={6} key={label}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{value || 'Not provided'}</Typography>
                    </Grid>
                  ))}
                </Grid>
                <Divider sx={{ my: 2 }} />
                {onboardingFormData.permanentAddress?.sameAsCurrent ? (
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>Permanent address same as current address</Typography>
                ) : (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Permanent Address</Typography>
                    <Grid container spacing={2}>
                      {[
                        ['Street', onboardingFormData.permanentAddress?.street],
                        ['City', onboardingFormData.permanentAddress?.city],
                        ['State', onboardingFormData.permanentAddress?.state],
                        ['Pin-code', onboardingFormData.permanentAddress?.zipCode],
                        ['Country', onboardingFormData.permanentAddress?.country],
                      ].map(([label, value]) => (
                        <Grid item xs={12} sm={6} key={label}>
                          <Typography variant="caption" color="text.secondary">{label}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{value || 'Not provided'}</Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Contact & Identity */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>Contact & Identity Documents</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {[
                    ['Email ID', onboardingFormData.emailId],
                    ['Mobile Number', onboardingFormData.mobileNumber],
                    ['Emergency Contact Name', onboardingFormData.emergencyContact?.name],
                    ['Emergency Contact Number', onboardingFormData.emergencyContact?.contactNo],
                    ['Relationship', onboardingFormData.emergencyContact?.relationshipWithEmployee],
                    ['Passport No.', onboardingFormData.passportNo],
                    ...(onboardingFormData.country === 'India'
                      ? [['Aadhaar Number', onboardingFormData.aadharNumber], ['PAN Card Number', onboardingFormData.panCardNumber], ['UAN Number', onboardingFormData.pfDetails?.uanNumber]]
                      : [['Tax Identification Number', onboardingFormData.taxIdentificationNumber], ['National Identity Number', onboardingFormData.nationalIdentityNumber], ['Social Security Number', onboardingFormData.pfDetails?.socialSecurityNumber]]
                    ),
                  ].map(([label, value]) => (
                    <Grid item xs={12} sm={6} key={label}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: label.includes('Number') || label.includes('Aadhaar') || label.includes('PAN') ? 'monospace' : 'inherit' }}>{value || 'Not provided'}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Work Experience */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>Work Experience ({(onboardingFormData.workExperiences || []).length})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {(onboardingFormData.workExperiences || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No work experience provided.</Typography>
                ) : (
                  (onboardingFormData.workExperiences || []).map((exp, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Work Experience {i + 1}</Typography>
                      <Grid container spacing={2}>
                        {[
                          ['Organization', exp.organizationName],
                          ['Designation', exp.designation],
                          ['From', exp.fromDate ? new Date(exp.fromDate).toLocaleDateString() : null],
                          ['To', exp.toDate ? new Date(exp.toDate).toLocaleDateString() : null],
                          ['Contact', exp.contactNumber],
                          ['Job Description', exp.jobDescription],
                        ].map(([label, value]) => (
                          <Grid item xs={12} sm={label === 'Job Description' ? 12 : 6} key={label}>
                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{value || 'Not provided'}</Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  ))
                )}
              </AccordionDetails>
            </Accordion>

            {/* Family Members */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>Family Members ({(onboardingFormData.familyMembers || []).length})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {(onboardingFormData.familyMembers || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No family members provided.</Typography>
                ) : (
                  (onboardingFormData.familyMembers || []).map((m, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        {[
                          ['Name', m.name],
                          ['Date of Birth', m.dateOfBirth ? new Date(m.dateOfBirth).toLocaleDateString() : null],
                          ['Relation', m.relation],
                          ['Residing with self', m.residingWithSelf ? 'Yes' : 'No'],
                        ].map(([label, value]) => (
                          <Grid item xs={12} sm={3} key={label}>
                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{value || 'Not provided'}</Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  ))
                )}
              </AccordionDetails>
            </Accordion>

            {/* Bank Details */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>Bank Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {[
                    ['Bank Name', onboardingFormData.bankDetails?.bankName],
                    ['Branch Address', onboardingFormData.bankDetails?.branchAddress],
                    ['Account Name', onboardingFormData.bankDetails?.accountName],
                    ['Account No', onboardingFormData.bankDetails?.accountNo],
                    onboardingFormData.country === 'India'
                      ? ['IFSC Code', onboardingFormData.bankDetails?.ifscCode]
                      : ['Swift Code / Bank Code', onboardingFormData.bankDetails?.swiftCode],
                  ].map(([label, value]) => (
                    <Grid item xs={12} sm={6} key={label}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{value || 'Not provided'}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Declaration */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>Declaration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={onboardingFormData.declaration ? 'Accepted' : 'Not Accepted'}
                    color={onboardingFormData.declaration ? 'success' : 'warning'}
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Employee declaration status
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </Card>

      {/* Country Selection */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Select Country for Process Documents
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} key="country-select">
            <FormControl fullWidth>
              <InputLabel>Select Country</InputLabel>
              <Select
                value={selectedCountry}
                onChange={handleCountryChange}
                label="Select Country"
              >
                {availableCountries.map((country) => (
                  <MenuItem key={country.value} value={country.value}>
                    {country.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {/* Process Documents Step - Country Specific Documents */}
      {selectedCountry && documentRequirements.length > 0 && (
        <Card sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: '20px', fontWeight: 600 }}>
            Process Documents - {selectedCountry} Requirements
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: '16px' }}>
            Upload the required documents for {selectedCountry}. Please ensure all mandatory documents are uploaded.
          </Typography>

          {/* Document Requirements */}
          <Alert severity="info" sx={{ mb: 3, '& .MuiAlert-icon': { alignSelf: 'flex-start', mt: 0.5 } }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '18px', fontWeight: 600 }}>
              Documents for {selectedCountry}:
            </Typography>
            {loadingDocumentRequirements ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={30} />
              </Box>
            ) : documentRequirements.length > 0 ? (
              <Grid container spacing={2}>
                {documentRequirements.map((docType) => (
                  <Grid item xs={12} sm={6} key={docType.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Checkbox
                        checked={processDocuments.some(doc => doc.documentName === docType.label)}
                        disabled
                        color="primary"
                      />
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '16px' }}>
                          {docType.label}
                          {docType.required && (
                            <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '14px' }}>
                          {docType.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No document requirements found for {selectedCountry}. Please add document requirements in the admin panel.
              </Typography>
            )}
          </Alert>

          {/* Upload Button */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => setUploadDialogOpen(true)}
              disabled={documentRequirements.length === 0 || loadingDocumentRequirements}
              sx={{ fontSize: '14px', fontWeight: 600 }}
            >
              Upload Process Document
            </Button>
          </Box>

          {/* Uploaded Documents */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '18px', fontWeight: 600 }}>
              Uploaded Documents
            </Typography>
            {loadingProcessDocuments ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : processDocuments.length === 0 ? (
              <Alert severity="info">
                No documents uploaded yet for {selectedCountry} requirements.
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {processDocuments.map((doc) => (
                  <Grid item xs={12} sm={6} md={4} key={doc.documentId || doc.id}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" component="div" sx={{ fontSize: '18px', fontWeight: 600 }}>
                          {doc.documentName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Uploaded: {(doc.uploadedAt || doc.uploadDate || doc.createdAt) 
                            ? new Date(doc.uploadedAt || doc.uploadDate || doc.createdAt).toLocaleDateString()
                            : 'Unknown'
                          }
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadDocument(doc)}
                          sx={{ fontSize: '14px', fontWeight: 600 }}
                        >
                          Download
                        </Button>
                        <Button
                          startIcon={<DeleteIcon />}
                          color="error"
                          onClick={() => handleDelete(doc.documentId || doc.id)}
                          sx={{ fontSize: '14px', fontWeight: 600 }}
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Card>
      )}

      {/* Employee Documents Section */}
      {user && (
        <Card sx={{ p: 3, mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: '20px', fontWeight: 600, mb: 1 }}>
                Employee Documents - {user.firstName} {user.lastName}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: '16px' }}>
              This document is uploaded by the HR.
              </Typography>

            </Box>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => setHrUploadDialogOpen(true)}
              sx={{ fontSize: '14px', fontWeight: 600 }}
            >
              Upload Employee Document
            </Button>
          </Box>

          {loadingHrDocuments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          ) : hrDocuments.length > 0 ? (
            <Grid container spacing={2}>
              {hrDocuments.map((doc) => (
                <Grid item xs={12} sm={6} md={4} key={doc._id}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="subtitle1" sx={{ fontSize: '16px', fontWeight: 'bold' }}>
                          {doc.documentName}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px', mb: 1 }}>
                        Uploaded: {(doc.createdAt || doc.uploadedAt || doc.uploadDate) 
                          ? new Date(doc.createdAt || doc.uploadedAt || doc.uploadDate).toLocaleDateString()
                          : 'Unknown'
                        }
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px' }}>
                        Size: {doc.fileSize ? `${(doc.fileSize / 1024).toFixed(1)} KB` : 'Unknown'}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadDocument(doc)}
                        sx={{ fontSize: '14px' }}
                      >
                        Download
                      </Button>
                      <Button
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDelete(doc._id)}
                        color="error"
                        sx={{ fontSize: '14px' }}
                      >
                        Delete
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Alert severity="info">
              No employee documents uploaded yet. Click "Upload Employee Document" to add documents.
            </Alert>
          )}
        </Card>
      )}

      {/* Show message when no country selected */}
      {!selectedCountry && (
        <Alert severity="info">
          Please select a country to manage process documents.
        </Alert>
      )}

      {/* Upload Document Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 600 }}>
          Upload Document
          <IconButton
            aria-label="close"
            onClick={() => setUploadDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={selectedDocumentType}
                onChange={(e) => setSelectedDocumentType(e.target.value)}
                label="Document Type"
                disabled={documentRequirements.length === 0}
              >
                {documentRequirements.length > 0 ? (
                  documentRequirements.map((docType) => (
                    <MenuItem key={docType.id} value={docType.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">
                          {docType.label}
                        </Typography>
                        {docType.required && (
                          <Chip label="Required" size="small" color="error" />
                        )}
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      No document requirements available
                    </Typography>
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <Box sx={{ mb: 2 }}>
              <input
                accept={
                  selectedDocumentType && 
                  documentRequirements.find(doc => doc.id === selectedDocumentType)?.imageOnly
                    ? "image/*"
                    : ".pdf,.jpg,.jpeg,.png,.gif,.webp"
                }
                style={{ display: 'none' }}
                id="document-file"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="document-file">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                >
                  Choose PDF or Image File
                </Button>
              </label>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {selectedDocumentType && 
                  documentRequirements.find(doc => doc.id === selectedDocumentType)?.imageOnly
                    ? "Accepted formats: JPG, JPEG, PNG, GIF, WEBP (Images only). Maximum file size: 10MB"
                    : "Accepted formats: PDF, JPG, JPEG, PNG, GIF, WEBP. Maximum file size: 10MB"
                }
              </Typography>
              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {file.name}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleProcessUpload}
            variant="contained"
            disabled={uploading || !file || !selectedDocumentType}
          >
            {uploading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Uploading...
              </>
            ) : (
              'Upload Document'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Employee Upload Document Dialog */}
      <Dialog
        open={hrUploadDialogOpen}
        onClose={() => setHrUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 600 }}>
          Upload Employee Document
          <IconButton
            aria-label="close"
            onClick={() => setHrUploadDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Employee Document Type</InputLabel>
              <Select
                value={selectedHrDocumentType}
                onChange={(e) => setSelectedHrDocumentType(e.target.value)}
                label="Employee Document Type"
              >
                {Object.entries(HR_DOCUMENT_TYPES).map(([key, docType]) => (
                  <MenuItem key={key} value={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontSize: '16px' }}>
                        {docType.name}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mb: 2 }}>
              <input
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                style={{ display: 'none' }}
                id="hr-document-file"
                type="file"
                onChange={handleHrFileChange}
              />
              <label htmlFor="hr-document-file">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                  sx={{ fontSize: '14px' }}
                >
                  Choose PDF or Image File
                </Button>
              </label>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontSize: '12px' }}>
                Accepted formats: PDF, JPG, JPEG, PNG, GIF, WEBP. Maximum file size: 10MB
              </Typography>
              {hrFile && (
                <Typography variant="body2" sx={{ mt: 1, fontSize: '14px' }}>
                  Selected: {hrFile.name}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHrUploadDialogOpen(false)} sx={{ fontSize: '14px' }}>Cancel</Button>
          <Button
            onClick={handleHrUpload}
            variant="contained"
            disabled={uploadingHr || !hrFile || !selectedHrDocumentType}
            sx={{ fontSize: '14px' }}
          >
            {uploadingHr ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Uploading...
              </>
            ) : (
              'Upload Employee Document'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDocumentsTab;
